/**
 * 사례 글의 서식(2026-10-24 사용자 요청 — 편집할 때 진하게·이탤릭·색·크기).
 *
 * 진하게·이탤릭·취소선·제목·목록·코드는 **마크다운 그대로**다(저장 형식이 바뀌지 않는다). 마크다운에 없는 색·크기는
 * 정해진 몇 가지만 쓰는 전용 표시 `{{키:글}}` 로 둔다 — 화면이 정해진 색·크기로만 그린다(HTML·스타일을 받는 길을 열지 않는다:
 * 범용 사례는 남에게도 보인다). 키 목록은 서버 `assistant/tuningCase/textMarks.js` 와 같아야 한다(테스트가 대조한다).
 */
export const MARK_KEYS = ['red', 'blue', 'green', 'mark', 'big', 'small'];
const MARK_SRC = `\\{\\{(${MARK_KEYS.join('|')}):([^{}\\n]+?)\\}\\}`;

/** 표시를 걷어 낸 글 */
export function stripMarks(text) {
  if (typeof text !== 'string' || !text.includes('{{')) return text;
  return text.replace(new RegExp(MARK_SRC, 'g'), '$2');
}

/** 글 조각 하나를 text / mark 노드들로 나눈다 */
function splitText(value) {
  const re = new RegExp(MARK_SRC, 'g');
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(value)) !== null) {
    if (m.index > last) out.push({ type: 'text', value: value.slice(last, m.index) });
    out.push({
      type: 'caseMark',
      data: { hName: 'span', hProperties: { className: [`rv-tx`, `rv-tx-${m[1]}`] } },
      children: [{ type: 'text', value: m[2] }],
    });
    last = m.index + m[0].length;
  }
  if (!out.length) return null;
  if (last < value.length) out.push({ type: 'text', value: value.slice(last) });
  return out;
}

/**
 * react-markdown 의 remark 플러그인 — 글(text) 노드 안의 `{{키:글}}` 을 정해진 class 의 span 으로 바꾼다.
 * 코드(code·inlineCode)는 text 노드가 아니라 손대지 않는다(SQL 속 `{{` 가 바뀌지 않게).
 */
export function remarkCaseMarks() {
  const walk = (node) => {
    if (!node || !Array.isArray(node.children)) return;
    const next = [];
    for (const child of node.children) {
      if (child.type === 'text' && typeof child.value === 'string' && child.value.includes('{{')) {
        const parts = splitText(child.value);
        if (parts) { next.push(...parts); continue; }
      }
      walk(child);
      next.push(child);
    }
    node.children = next;
  };
  return (tree) => { walk(tree); };
}

/** text[i] 에서 왼쪽(dir -1)·오른쪽(dir 1)으로 이어진 같은 글자 수 */
function runOf(text, i, ch, dir) {
  let n = 0;
  for (let k = i; k >= 0 && k < text.length && text[k] === ch; k += dir) n += 1;
  return n;
}

/**
 * 이미 걸린 서식을 푼다 — 풀 것이 없으면 null.
 *  · 진하게(**)·이탤릭(*): 별표가 겹칠 수 있어(`***글***` = 진하게+이탤릭) **이어진 별표 수**로 판정한다 —
 *    진하게는 양쪽 2개 이상, 이탤릭은 양쪽 홀수(1·3). 진하게 안의 글을 고르고 이탤릭을 눌러도 진하게가 이탤릭으로 바뀌지 않는다
 *  · 색·크기: 바로 앞이 `{{키:` 이고 뒤가 `}}` — 같은 키면 풀고, 다른 키면 그 키로 바꾼다(빨강 → 파랑)
 *  · 그 밖(취소선·코드): 앞뒤가 그 표시인지
 * 고른 범위가 표시까지 품고 있으면(`**글**` 를 통째로 골랐을 때) 안쪽 기준으로 다시 본다.
 */
function unwrapRange(text, s, e, { kind, open, close, key }) {
  const sel = text.slice(s, e);
  if (kind === 'mark') {
    const head = /\{\{([a-z]+):$/.exec(text.slice(Math.max(0, s - 12), s));
    if (head && MARK_KEYS.includes(head[1]) && text.startsWith('}}', e)) {
      const hs = s - head[0].length;
      if (head[1] === key) {
        return { value: text.slice(0, hs) + sel + text.slice(e + 2), start: hs, end: hs + sel.length };
      }
      const swapped = `{{${key}:`;
      return { value: text.slice(0, hs) + swapped + text.slice(s), start: hs + swapped.length, end: hs + swapped.length + sel.length };
    }
    const whole = /^\{\{([a-z]+):([^{}\n]*)\}\}$/.exec(sel);
    if (whole && MARK_KEYS.includes(whole[1])) {
      if (whole[1] === key) return { value: text.slice(0, s) + whole[2] + text.slice(e), start: s, end: s + whole[2].length };
      const re = `{{${key}:${whole[2]}}}`;
      return { value: text.slice(0, s) + re + text.slice(e), start: s, end: s + re.length };
    }
    return null;
  }
  if (kind !== 'wrap' || !open) return null;
  if (open === '*' || open === '**') {
    const w = open.length;
    const has = (l, r) => (w === 2 ? l >= 2 && r >= 2 : l % 2 === 1 && r % 2 === 1);
    // ① 고른 범위가 별표까지 품고 있을 때(`**글**` 를 통째로 고름) — 범위 안의 앞뒤 별표 수로
    const lead = runOf(sel, 0, '*', 1);
    const trail = runOf(sel, sel.length - 1, '*', -1);
    if (lead && trail && lead + trail < sel.length && has(lead, trail)) {
      // 글에 붙은 쪽 별표부터 뗀다(`***글***` 에서 이탤릭을 풀면 `**글**`)
      const next = sel.slice(0, lead - w) + sel.slice(lead, sel.length - trail) + sel.slice(sel.length - trail + w);
      return { value: text.slice(0, s) + next + text.slice(e), start: s, end: s + next.length };
    }
    // ② 글만 골랐을 때 — 바로 앞뒤에 이어진 별표 수로
    const left = runOf(text, s - 1, '*', -1);
    const right = runOf(text, e, '*', 1);
    if (!has(left, right)) return null;
    const value = text.slice(0, s - w) + sel + text.slice(e + w);
    return { value, start: s - w, end: s - w + sel.length };
  }
  // 취소선·코드 — 고른 범위가 표시를 품고 있거나, 바로 앞뒤가 표시
  if (sel.length > open.length + close.length && sel.startsWith(open) && sel.endsWith(close)) {
    const inner = sel.slice(open.length, sel.length - close.length);
    return { value: text.slice(0, s) + inner + text.slice(e), start: s, end: s + inner.length };
  }
  if (s >= open.length && text.slice(s - open.length, s) === open && text.startsWith(close, e)) {
    const value = text.slice(0, s - open.length) + sel + text.slice(e + close.length);
    return { value, start: s - open.length, end: s - open.length + sel.length };
  }
  return null;
}

/**
 * 서식 넣기 — 편집 칸의 고른 범위에 적용한 새 글과 그 뒤의 선택 범위를 돌려준다(순수 함수).
 * kind: 'wrap'(앞뒤로 감싼다 — 진하게 등) · 'mark'(색·크기 표시) · 'line'(고른 줄마다 앞에 붙인다 — 제목·목록)
 * 고른 글이 없으면 자리 표시 글을 넣고 그것을 고른다(바로 덮어 쓸 수 있게).
 */
export function applyFormat(value, start, end, { kind, before = '', after = '', key, placeholder = '' }) {
  const text = String(value || '');
  const s = Math.max(0, Math.min(start, text.length));
  const e = Math.max(s, Math.min(end, text.length));
  if (kind === 'line') {
    const lineStart = text.lastIndexOf('\n', s - 1) + 1;
    const lineEndIdx = text.indexOf('\n', e > s && text[e - 1] === '\n' ? e - 1 : e);
    const lineEnd = lineEndIdx < 0 ? text.length : lineEndIdx;
    const block = text.slice(lineStart, lineEnd);
    const lines = block.split('\n');
    // 모든 줄에 이미 붙어 있으면 뗀다(토글)
    const allHave = lines.every(l => l.startsWith(before));
    const changed = lines.map(l => (allHave ? l.slice(before.length) : `${before}${l}`)).join('\n');
    return { value: text.slice(0, lineStart) + changed + text.slice(lineEnd), start: lineStart, end: lineStart + changed.length };
  }
  const open = kind === 'mark' ? `{{${key}:` : before;
  const close = kind === 'mark' ? '}}' : after;
  // **이미 그 서식이면 푼다(토글, 2026-10-24 신고)** — 고른 글의 바로 앞뒤에 표시가 있거나, 고른 범위가 표시까지 품고 있을 때
  const un = unwrapRange(text, s, e, { kind, open, close, key });
  if (un) return un;
  let inner = text.slice(s, e);
  // 색·크기 표시는 한 줄 안에서만 — 여러 줄을 고르면 줄마다 따로 감싼다
  if (kind === 'mark' && inner.includes('\n')) {
    const wrapped = inner.split('\n').map(l => (l.trim() ? `${open}${l}${close}` : l)).join('\n');
    return { value: text.slice(0, s) + wrapped + text.slice(e), start: s, end: s + wrapped.length };
  }
  if (!inner) inner = placeholder;
  const next = text.slice(0, s) + open + inner + close + text.slice(e);
  return { value: next, start: s + open.length, end: s + open.length + inner.length };
}
